import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Star, Play, Loader2, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

interface GameData {
  id: string;
  title: string;
  provider: string;
  image: string;
  rating: number;
  category: string;
  description: string;
  game_url?: string;
}

const Game: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [launching, setLaunching] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const fetchGameDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/games/${id}`);
        if (response.data && response.data.success) {
          setGame(response.data.game);
        } else {
          throw new Error(response.data.message || 'Jogo não encontrado.');
        }
      } catch (err) {
        console.error("Error fetching game details:", err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchGameDetails();
    }
  }, [id]);

  // Efeito para ativar a tela cheia em dispositivos móveis
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (launchUrl && isMobile && iframeRef.current) {
      try {
        iframeRef.current.requestFullscreen();
      } catch (err) {
        console.error("Erro ao tentar entrar em tela cheia:", err);
      }
    }
  }, [launchUrl]);

  const handleLaunchGame = async () => {
    setLaunching(true);
    setError(null);
    try {
      const response = await api.post(`/games/${id}/launch`);
      if (response.data && response.data.success) {
        setLaunchUrl(response.data.game_url);
      } else {
        throw new Error(response.data.message || 'Não foi possível iniciar o jogo.');
      }
    } catch (err) {
      console.error("Error launching game:", err);
      setError((err as Error).message);
      toast({
          title: "Error launching game!",
          description: `${err}!`,
      });
    } finally {
      setLaunching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="ml-3 text-lg">Carregando jogo...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-16 text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">Erro</h1>
        <p className="text-muted-foreground text-lg mb-8">
          {error === 'Game not found' ? 'O jogo que você está procurando não existe ou foi removido.' : `Ocorreu um erro: ${error}`}
        </p>
        <Button asChild>
          <Link to="/games">Voltar para a Página Inicial</Link>
        </Button>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="container mx-auto py-16 text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">Jogo não encontrado</h1>
        <p className="text-muted-foreground text-lg mb-8">
          O jogo que você está procurando não existe ou foi removido.
        </p>
        <Button asChild>
          <Link to="/">Voltar para a Página Inicial</Link>
        </Button>
      </div>
    );
  }

  if (launchUrl) {
    return (
      <div className="min-h-screen bg-background py-8 md:py-12">
        <div className="container mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {game.title}
          </h1>
          <p className="text-muted-foreground mb-4">
            Carregando o jogo... Se o jogo não aparecer, verifique as permissões do navegador para iframes.
          </p>
        </div>
        <div className="relative w-full h-[80vh] bg-black shadow-xl">
          <iframe
            ref={iframeRef}
            src={launchUrl}
            title={game.title}
            className="w-full h-full border-0"
            allowFullScreen
          ></iframe>
        </div>
        <div className="container mx-auto text-center">
          <Button variant="outline" onClick={() => setLaunchUrl(null)} className="mt-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Lobby
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={game.image}
            alt={game.title}
            className="w-full h-full object-cover blur-sm opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Miniatura */}
            <div className="flex-shrink-0 w-2/3 mx-auto md:mx-0 md:w-1/3 lg:w-1/4">
              <img
                src={game.image}
                alt={game.title}
                className="w-full h-auto rounded-lg shadow-lg border border-border"
              />
            </div>
            <div className="flex-grow text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                {game.title}
              </h1>
              <p className="text-lg text-muted-foreground mb-2">
                Provedor: <span className="font-semibold text-primary">{game.provider}</span>
              </p>
              <div className="flex items-center justify-center md:justify-start text-lg text-accent mb-4">
                <Star className="w-5 h-5 fill-accent mr-1" /> {game.rating}
              </div>
              <p className="text-md text-foreground mb-6">
                {game.description} 
              </p>
              <div className="p-2">
                <Button
                  size="lg"
                  className="bg-gradient-primary hover:shadow-glow transition-all duration-300 text-lg px-8 py-6 m-2 mt-2"
                  onClick={handleLaunchGame}
                  disabled={launching}
                >
                  {launching ? (
                    <Loader2 className="w-5 h-5 m-2 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 m-2" fill="currentColor" />
                  )}
                  {launching ? 'Iniciando...' : 'Jogar Agora'}
                </Button>

                <Button
                  size="lg"
                  className="bg-gradient-primary hover:shadow-glow transition-all duration-300 text-lg px-8 py-6 m-2 mt-2"
                  >
                  <ArrowLeft className="w-5 h-5 m-2" />
                  <Link to="/games">Voltar ao Lobby</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Games Section (Placeholder) */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
            Jogos Relacionados
          </h2>
          <p className="text-center text-muted-foreground">
            (Em breve: uma lista de jogos similares ou recomendados)
          </p>
        </div>
      </section>
    </div>
  );
};

export default Game;

